import type { UseFormReturn } from 'react-hook-form';

import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { OrganisationAddress } from '~/api/clients/types';

import { Card } from '~/components';
import { Checkbox, Field, Select } from '~/components/Form';

import type { ContactFormType } from '../types';

import { ContactFormSection } from './ContactFormSection';
import {
  addressItemStyle,
  addressItemSublabelStyle,
  fullWidthStyle,
  gridStyle,
  organisationAddressStyle,
  organisationAddressTypeStyle,
} from './contactFormSections.css';
import { FormAddressCity } from './FormAddressCity';
import { FormAddressNumber } from './FormAddressNumber';
import { FormAddressStreet } from './FormAddressStreet';
import { FormAddressZipCode } from './FormAddressZipCode';
import { FormCountryCode } from './FormCountryCode';

type ContactAddressFormType = Pick<ContactFormType, 'address' | 'addressIndex' | 'useOrganisationAddress'>;

export const ContactAddressFormSection = <T extends ContactAddressFormType>({
  addresses,
  form: { control, watch },
}: {
  addresses: OrganisationAddress[];
  form: T extends ContactAddressFormType ? UseFormReturn<T> : never;
}) => {
  const watchUseOrganisationAddress = watch('useOrganisationAddress');

  const { t } = useTranslation(['clients', 'validation']);

  const addressItems = useMemo(
    () =>
      addresses.map((address, index) => ({
        label: `${address.street} ${address.number}, ${address.zipCode} ${address.city}`,
        sublabel: t(`clients:addressType.${address.type}`),
        value: index,
      })),
    [addresses, t],
  );

  return (
    <ContactFormSection title={t('clients:createEdit.subsections.contactAddress')}>
      <div className={gridStyle}>
        {/* Toggle address type */}
        {addresses.length > 0 && (
          <div className={fullWidthStyle}>
            <Controller
              control={control}
              name="useOrganisationAddress"
              render={({ field: { name, onBlur, onChange, ref, value } }) => (
                <Field.Root name={name}>
                  <Checkbox.Root checked={value} onBlur={onBlur} onCheckedChange={onChange} ref={ref}>
                    <Checkbox.Label>{t('clients:fields.useOrganisationAddress.label')}</Checkbox.Label>
                  </Checkbox.Root>
                </Field.Root>
              )}
            />
          </div>
        )}

        {watchUseOrganisationAddress && addresses.length > 0 ? (
          // Organisation address(es)
          addresses.length === 1 ? (
            <Card.Root className={organisationAddressStyle} variant="primary-accent">
              <span className={organisationAddressTypeStyle}>{t(`clients:addressType.${addresses[0].type}`)}</span>

              <span>
                {addresses[0].street} {addresses[0].number}, {addresses[0].zipCode} {addresses[0].city}
              </span>
            </Card.Root>
          ) : (
            <Controller
              control={control}
              name="addressIndex"
              render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
                <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                  <Field.Label>{t('clients:fields.address.label')}</Field.Label>

                  <Select.Root items={addressItems} onValueChange={onChange} value={value}>
                    <Select.Button onBlur={onBlur} ref={ref} />

                    <Select.List>
                      {addressItems.map((item) => (
                        <Select.Item key={item.value} value={item.value}>
                          <span className={addressItemStyle}>
                            {item.label}
                            <span className={addressItemSublabelStyle}>{item.sublabel}</span>
                          </span>
                        </Select.Item>
                      ))}
                    </Select.List>
                  </Select.Root>

                  <Field.Error match={!!error}>{error?.message}</Field.Error>
                </Field.Root>
              )}
            />
          )
        ) : (
          // Contact address
          <>
            <FormAddressStreet control={control} />
            <FormAddressNumber control={control} />
            <FormAddressZipCode control={control} />
            <FormAddressCity control={control} />
            <FormCountryCode control={control} />
          </>
        )}
      </div>
    </ContactFormSection>
  );
};
